import { IonContent } from '@ionic/react';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import { amplitudeModule } from '@/modules/amplitude';
import { webCampaign } from '@/modules/webCampaign';
import { OnboardingNavigation } from '@/pages/Onboarding/components/Navigation';
import {
  OptionItemSprite,
  OptionItemSpriteList,
} from '@/pages/Onboarding/components/OptionItemSprite/OptionItemSprite';
import { useOnboarding } from '@/pages/Onboarding/context';
import { OnbOptionType } from '@/pages/Onboarding/types';
import { nextStepTimeout } from '@/pages/Onboarding/utils/nextStepTimeout';
import { userService } from '@/services/user';
import { canLogEvents } from '@/utils/canLogEvents';
import { preloadImage } from '@/utils/preloadImage';

import { OwnAiTitle, OwnAiTitleHighlight } from '../../components/Title';
import { LayoutDataType } from '../../types';

import { ETHNICITY_OPTIONS } from './constants';
import { ScreenEthnicityStyles } from './Ethnicity.styles';

export const ScreenEthnicity: React.FC = () => {
  React.useEffect(() => {
    if (canLogEvents()) {
      amplitudeModule.logEvent('onboarding_screen', {
        seen: 'Ethnicity',
      });
    }
  }, []);

  const { wizard } = useOnboarding();

  const form = useFormContext<LayoutDataType>();

  const ethnicity = form.watch('ethnicity');
  const age = form.watch('age') || '18-24';

  const handleSelect = (option: OnbOptionType<LayoutDataType['ethnicity']>) => {
    form.setValue('ethnicity', option.id);
    userService.createOrUpdate({
      onboarding_data: {
        ethnicity: option.title,
      },
    });
    nextStepTimeout(wizard.nextStep);
  };

  React.useEffect(() => {
    if (webCampaign.getLayoutNotes().includes('three-screens-new')) {
      ETHNICITY_OPTIONS.forEach((option) => {
        preloadImage(
          `https://d3hhlnlnie19wm.cloudfront.net/web-onb/assets/layout-own-ai/three-screens-new/by_choice/${age}-${option.id}-1.png`,
        );
        preloadImage(
          `https://d3hhlnlnie19wm.cloudfront.net/web-onb/assets/layout-own-ai/three-screens-new/by_choice/${age}-${option.id}-2.png`,
        );
        preloadImage(
          `https://d3hhlnlnie19wm.cloudfront.net/web-onb/assets/layout-own-ai/three-screens-new/by_choice/${age}-${option.id}-3.png`,
        );
        preloadImage(
          `https://d3hhlnlnie19wm.cloudfront.net/web-onb/assets/layout-own-ai/three-screens-new/by_choice/${age}-${option.id}-4.png`,
        );
      });
    } else {
      ETHNICITY_OPTIONS.forEach((option) => {
        preloadImage(
          `https://d3hhlnlnie19wm.cloudfront.net/web-onb/assets/layout-own-ai/ethnicity/${option.id}/${age}-1.png`,
        );
        preloadImage(
          `https://d3hhlnlnie19wm.cloudfront.net/web-onb/assets/layout-own-ai/ethnicity/${option.id}/${age}-2.png`,
        );
        preloadImage(
          `https://d3hhlnlnie19wm.cloudfront.net/web-onb/assets/layout-own-ai/ethnicity/${option.id}/${age}-3.png`,
        );
        preloadImage(
          `https://d3hhlnlnie19wm.cloudfront.net/web-onb/assets/layout-own-ai/ethnicity/${option.id}/${age}-4.png`,
        );
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getImageSrc = (ethnicityId: string) => {
    if (webCampaign.getLayoutNotes().includes('three-screens-new')) {
      return `https://d3hhlnlnie19wm.cloudfront.net/web-onb/assets/layout-own-ai/three-screens-new/ethnicity/sprite-${age}-${ethnicityId}.png`;
    }

    return `https://d3hhlnlnie19wm.cloudfront.net/web-onb/assets/layout-own-ai/ethnicity/${ethnicityId}/sprite-${age}.png`;
  };

  return (
    <ScreenEthnicityStyles>
      <OnboardingNavigation />
      <OwnAiTitle>
        Select her <OwnAiTitleHighlight>ethnicity</OwnAiTitleHighlight>
      </OwnAiTitle>

      <IonContent>
        <OptionItemSpriteList>
          {ETHNICITY_OPTIONS.map((option) => (
            <OptionItemSprite
              key={option.id}
              alt="ethnicity"
              isSelected={ethnicity === option.id}
              onClick={() => handleSelect(option)}
              spriteSrc={getImageSrc(option.id)}
              title={option.title}
              TEST_ID={`ethnicity-${option.id}`}
              show
            />
          ))}
        </OptionItemSpriteList>
      </IonContent>
    </ScreenEthnicityStyles>
  );
};
